import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import { 
  solicitarCodigoAcessoDocumentos, 
  validarCodigoAcessoDocumentos,
  acompanharDocumento 
} from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AcompanhamentoPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'cpf' | 'codigo'>('cpf');
  const [cpf, setCpf] = useState('');
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [servidorInfo, setServidorInfo] = useState<any>(null);
  const [codigoEnviado, setCodigoEnviado] = useState(false);
  const [documentoInfo, setDocumentoInfo] = useState<any>(null);
  
  // Ref para controlar se já estamos navegando
  const isNavigating = useRef(false);

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0,3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0,3)}.${numbers.slice(3,6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0,3)}.${numbers.slice(3,6)}.${numbers.slice(6,9)}-${numbers.slice(9,11)}`;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
    setError('');
  };

  const handleCodigoChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCodigo = [...codigo];
      newCodigo[index] = value;
      setCodigo(newCodigo);
      setError('');
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-input-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  // Opção 1: Solicitar código com CPF
  const handleSolicitarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cleanedCPF = cpf.replace(/\D/g, '');
    if (cleanedCPF.length !== 11) {
      setError('CPF inválido. Digite 11 números.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      toast.loading('Enviando código para seu email...', { id: 'send-code' });
      const response = await solicitarCodigoAcessoDocumentos(cleanedCPF);
      
      setServidorInfo(response.servidor);
      setCodigoEnviado(true);
      setActiveTab('codigo');
      
      toast.success(`Código enviado para: ${response.servidor.email}`, { id: 'send-code' });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Erro ao enviar código';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'send-code' });
    } finally {
      setLoading(false);
    }
  };

  // Opção 2: Validar código e navegar
  const handleValidarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevenir múltiplos cliques
    if (isNavigating.current) return;
    
    const codigoCompleto = codigo.join('');
    if (codigoCompleto.length !== 6) {
      setError('Digite todos os 6 dígitos do código');
      return;
    }

    setError('');
    setLoading(true);
    isNavigating.current = true;

    try {
      toast.loading('Validando código...', { id: 'validate-code' });
      const response = await validarCodigoAcessoDocumentos(codigoCompleto);
      
      // Salvar dados de sessão
      if (response.session_token) {
        localStorage.setItem('doc_session_token', response.session_token);
      }
      if (response.servidor?.id) {
        localStorage.setItem('servidorData', JSON.stringify(response.servidor));
        
        // Limpar o toast antes de navegar
        toast.dismiss('validate-code');
        toast.success('Acesso liberado! Redirecionando...', { 
          id: 'validate-code-success',
          duration: 2000 
        });
        
        // Pequeno delay para garantir que o toast seja visto
        setTimeout(() => {
          // Navegar para página de documentos
          navigate(`/documentos/${response.servidor.id}`, { replace: true });
        }, 500);
        
      } else {
        throw new Error('Servidor ID não encontrado na resposta');
      }
      
    } catch (err: any) {
      isNavigating.current = false;
      const errorMsg = err.response?.data?.message || 'Erro ao validar código';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'validate-code' });
    } finally {
      setLoading(false);
    }
  };

  // Opção 3: Acompanhar com código antigo
  const handleAcompanharDocumento = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isNavigating.current) return;
    
    const codigoCompleto = codigo.join('');
    if (codigoCompleto.length !== 6) {
      setError('Digite todos os 6 dígitos do código');
      return;
    }

    setError('');
    setLoading(true);
    isNavigating.current = true;

    try {
      toast.loading('Buscando documento...', { id: 'acompanhar' });
      const response = await acompanharDocumento(codigoCompleto);
      
      if (response.documento && response.servidor) {
        // Salvar dados
        localStorage.setItem('servidorData', JSON.stringify(response.servidor));
        localStorage.setItem('documentoAcompanhamento', JSON.stringify(response.documento));
        
        // Limpar toast e navegar
        toast.dismiss('acompanhar');
        toast.success('Documento encontrado! Redirecionando...', { 
          id: 'acompanhar-success',
          duration: 2000 
        });
        
        setTimeout(() => {
          navigate(`/documentos/${response.servidor.id}`, { replace: true });
        }, 500);
        
      } else {
        throw new Error('Dados do documento não encontrados');
      }
    } catch (err: any) {
      isNavigating.current = false;
      const errorMsg = err.response?.data?.message || 'Erro ao buscar documento';
      setError(errorMsg);
      toast.error(errorMsg, { id: 'acompanhar' });
    } finally {
      setLoading(false);
    }
  };

  const handleReenviarCodigo = async () => {
    if (!cpf || isNavigating.current) return;
    
    const cleanedCPF = cpf.replace(/\D/g, '');
    if (cleanedCPF.length !== 11) return;

    try {
      toast.loading('Reenviando código...', { id: 'resend-code' });
      await solicitarCodigoAcessoDocumentos(cleanedCPF);
      toast.success('Código reenviado com sucesso!', { id: 'resend-code' });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao reenviar código', { id: 'resend-code' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <KeyIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              mb: 2 
            }} 
          />
          
          <Typography variant="h5" gutterBottom color="primary.main">
            Acompanhar Documentos
          </Typography>
          
          <Typography variant="body1" paragraph color="text.secondary">
            Acompanhe o status dos seus documentos assinados
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, value) => {
                setActiveTab(value);
                setError('');
              }}
              centered
            >
              <Tab 
                label="CPF" 
                value="cpf"
                icon={<PersonIcon />}
                iconPosition="start"
              />
              <Tab 
                label="Código" 
                value="codigo"
                icon={<KeyIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {activeTab === 'cpf' ? (
            <>
              <Typography variant="body1" paragraph color="text.secondary">
                Digite seu CPF para receber um código de acesso por email.
              </Typography>

              <form onSubmit={handleSolicitarCodigo}>
                <TextField
                  fullWidth
                  label="Digite seu CPF"
                  value={cpf}
                  onChange={handleCPFChange}
                  placeholder="000.000.000-00"
                  variant="outlined"
                  margin="normal"
                  required
                  disabled={loading || isNavigating.current}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 }
                  }}
                  helperText="Use o mesmo CPF usado na consulta"
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || cpf.replace(/\D/g, '').length !== 11 || isNavigating.current}
                  sx={{ 
                    mt: 3, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Enviando...' : 'Receber Código por Email'}
                </Button>
              </form>

              {codigoEnviado && servidorInfo && (
              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: '#e8f4fd',
                  border: '1px solid #90caf9',
                }}
              >
                <Typography variant="body2" color="#1565c0" gutterBottom>
                  Código enviado para: <strong>{servidorInfo.email}</strong>
                </Typography>
                <Typography variant="body2" color="#1565c0">
                  Olá, <strong>{servidorInfo.nome_completo}</strong>
                </Typography>
                <Button
                  size="small"
                  onClick={handleReenviarCodigo}
                  sx={{ mt: 1, color: '#1976d2', textTransform: 'none', p: 0 }}
                  disabled={isNavigating.current}
                >
                  Não recebeu o código? Reenviar
                </Button>
              </Box>
            )}
            </>
          ) : (
            <>
              <Typography variant="body1" paragraph color="text.secondary">
                Digite o código de 6 dígitos recebido por email.
              </Typography>

              <form onSubmit={handleValidarCodigo}>
                <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                  {codigo.map((digit, index) => (
                    <Grid item key={index}>
                      <TextField
                        id={`code-input-${index}`}
                        value={digit}
                        onChange={(e) => handleCodigoChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: { 
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }
                        }}
                        sx={{
                          width: 50,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                        disabled={loading || isNavigating.current}
                        autoFocus={index === 0}
                      />
                    </Grid>
                  ))}
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || codigo.join('').length !== 6 || isNavigating.current}
                  sx={{ 
                    mt: 2, 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Validando...' : 'Acessar Meus Documentos'}
                </Button>
              </form>

              <Box sx={{ mt: 3 }}>
                <Button
                  onClick={handleReenviarCodigo}
                  variant="text"
                  size="small"
                  disabled={loading || !cpf || isNavigating.current}
                >
                  Não recebeu o código? Clique para reenviar
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                <strong>Importante:</strong> O código expira em 15 minutos
              </Typography>

              {/* Informações do servidor quando código foi enviado */}
              {codigoEnviado && servidorInfo && (
                <Card sx={{ mt: 3, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Código enviado para:
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>{servidorInfo.nome_completo}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {servidorInfo.email}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Seção para código de documento antigo (opcional) */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle1" gutterBottom color="text.secondary">
              Tem o código do documento?
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Se você tem o código específico do documento (enviado por email após assinatura),
              pode usá-lo diretamente:
            </Typography>

            <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
              {codigo.map((digit, index) => (
                <Grid item key={index}>
                  <TextField
                    id={`doc-code-input-${index}`}
                    value={digit}
                    onChange={(e) => handleCodigoChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    inputProps={{
                      maxLength: 1,
                      style: { 
                        textAlign: 'center',
                        fontSize: '1.2rem',
                      }
                    }}
                    sx={{
                      width: 40,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                    disabled={loading || isNavigating.current}
                  />
                </Grid>
              ))}
            </Grid>

            <Button
              fullWidth
              variant="outlined"
              size="medium"
              onClick={handleAcompanharDocumento}
              disabled={loading || codigo.join('').length !== 6 || isNavigating.current}
              sx={{ 
                mt: 1,
                borderRadius: 2
              }}
              startIcon={<DescriptionIcon />}
            >
              Acompanhar Documento Específico
            </Button>
          </Box>
        </Paper>

        {/* Informações sobre documentos encontrados */}
        {documentoInfo && (
          <Fade in={true} timeout={500}>
            <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Documento Encontrado
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {documentoInfo.documento?.nome_arquivo}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        Status: <strong>{documentoInfo.documento?.status}</strong>
                      </Typography>
                      
                      {documentoInfo.documento?.data_assinatura && (
                        <Typography variant="body2" color="text.secondary">
                          Assinado em: {formatDate(documentoInfo.documento.data_assinatura)}
                        </Typography>
                      )}
                      
                      {documentoInfo.servidor && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Servidor: {documentoInfo.servidor.nome}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}
      </Box>
    </Fade>
  );
};

export default AcompanhamentoPage;